import { StockLevel, Warehouse } from '@models/warehouse.model';

/**
 * Stock lives per (catalog, warehouse) pair. Any picker that scopes itself to a
 * single warehouse cannot tell "this material is exhausted everywhere" apart
 * from "this material simply lives in another warehouse" — both render as zero.
 *
 * These helpers aggregate stock levels across every warehouse so callers can
 * show the real org-wide picture and point the user at the warehouses that
 * actually hold the material.
 */

export interface WarehouseAvailability {
  warehouseId: string;
  warehouseName: string;
  quantityOnHand: number;
  quantityReserved: number;
  quantityAvailable: number;
  stockLevel: StockLevel;
}

export interface CatalogAvailability {
  catalogId: string;
  /** Sum of available quantity across every warehouse. */
  totalAvailable: number;
  /** Every warehouse holding a stock row, richest first. */
  byWarehouse: WarehouseAvailability[];
  /** Subset of `byWarehouse` with available > 0. */
  warehousesWithStock: WarehouseAvailability[];
}

export type AvailabilityIndex = Map<string, CatalogAvailability>;

/**
 * The API exposes `quantityAvailable`, but not every query selects it — derive
 * it so the number is correct regardless of the fragment used.
 */
export const availableOf = (stockLevel: StockLevel): number =>
  Number(stockLevel.quantityOnHand ?? 0) - Number(stockLevel.quantityReserved ?? 0);

const emptyAvailability = (catalogId: string): CatalogAvailability => ({
  catalogId,
  totalAvailable: 0,
  byWarehouse: [],
  warehousesWithStock: [],
});

/**
 * Indexes stock levels by catalogId. Pass the org-wide stock levels (i.e.
 * `useStockLevels()` with no warehouse filter) — a warehouse-scoped list gives
 * back exactly the misleading single-warehouse view this exists to avoid.
 */
export const buildAvailabilityIndex = (
  stockLevels: StockLevel[],
  warehouses: Warehouse[] = [],
): AvailabilityIndex => {
  const warehouseNames = new Map(warehouses.map((w) => [w.warehouseId, w.name]));
  const index: AvailabilityIndex = new Map();

  stockLevels.forEach((stockLevel) => {
    const quantityAvailable = availableOf(stockLevel);
    const entry = index.get(stockLevel.catalogId) ?? emptyAvailability(stockLevel.catalogId);

    entry.byWarehouse.push({
      warehouseId: stockLevel.warehouseId,
      warehouseName:
        stockLevel.warehouse?.name ||
        warehouseNames.get(stockLevel.warehouseId) ||
        'Unknown warehouse',
      quantityOnHand: Number(stockLevel.quantityOnHand ?? 0),
      quantityReserved: Number(stockLevel.quantityReserved ?? 0),
      quantityAvailable,
      stockLevel,
    });
    entry.totalAvailable += quantityAvailable;

    index.set(stockLevel.catalogId, entry);
  });

  index.forEach((entry) => {
    entry.byWarehouse.sort((a, b) => b.quantityAvailable - a.quantityAvailable);
    entry.warehousesWithStock = entry.byWarehouse.filter((w) => w.quantityAvailable > 0);
  });

  return index;
};

export const getCatalogAvailability = (
  index: AvailabilityIndex | undefined,
  catalogId: string,
): CatalogAvailability => index?.get(catalogId) ?? emptyAvailability(catalogId);

/**
 * "45 in Main Warehouse, 12 in Yard B" — used to explain a zero in the currently
 * selected warehouse instead of just showing "Out of Stock".
 */
export const describeStockElsewhere = (
  availability: CatalogAvailability,
  excludeWarehouseId?: string,
  maxNamed = 2,
): string => {
  const elsewhere = availability.warehousesWithStock.filter(
    (w) => w.warehouseId !== excludeWarehouseId,
  );
  if (elsewhere.length === 0) return '';

  const named = elsewhere
    .slice(0, maxNamed)
    .map((w) => `${w.quantityAvailable} in ${w.warehouseName}`)
    .join(', ');

  const remaining = elsewhere.length - maxNamed;
  return remaining > 0 ? `${named} +${remaining} more` : named;
};

/**
 * Picks the warehouse a reservation should default to: the one holding the most
 * stock, falling back to the org default so the form is never left empty.
 */
export const suggestWarehouseId = (
  availability: CatalogAvailability,
  warehouses: Warehouse[] = [],
): string | undefined =>
  availability.warehousesWithStock[0]?.warehouseId ??
  warehouses.find((w) => w.isDefault && w.isActive)?.warehouseId ??
  warehouses.find((w) => w.isActive)?.warehouseId;
